import React, { useState } from 'react';
import { 
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotographIcon
} from '@heroicons/react/outline';

const ImageGallery = ({ images = [], className = "" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Debug: Check if images are being passed correctly
  if (images && images.length > 0) {
    console.log('ImageGallery: Processing', images.length, 'images');
  }

  if (!images || images.length === 0) {
    return null;
  }

  const openModal = (index = 0) => {
    console.log('Opening modal for image index:', index);
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    console.log('Closing modal');
    setIsModalOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    }
  };

  // Get image URL - handle both string URLs and object with uri property
  const getImageUrl = (image) => {
    if (typeof image === 'string') {
      return image;
    }
    if (image && image.uri) {
      // Check if it's a full URL or just a filename
      if (image.uri.startsWith('http://') || image.uri.startsWith('https://')) {
        return image.uri;
      } else {
        // It's a filename, construct AWS S3 URL
        const url = `https://twercestore14549-devel.s3.eu-west-2.amazonaws.com/public/${image.uri}`;
        return url;
      }
    }
    return null;
  };

  const currentImage = images[currentImageIndex];
  const currentImageUrl = getImageUrl(currentImage);

  console.log('Modal state:', { isModalOpen, currentImageIndex, currentImageUrl });

  return (
    <>
      {/* Image Gallery Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
        {images.slice(0, 6).map((image, index) => {
          const imageUrl = getImageUrl(image);
          console.log(`Rendering image ${index}:`, { image, imageUrl });
          if (!imageUrl) return null;

          return (
            <div key={index} className="relative group">
              <img
                src={imageUrl}
                alt={`RFQ ${index + 1}`}
                className="w-full h-48 object-cover rounded-lg cursor-pointer transition-transform duration-200 hover:scale-105"
                onClick={() => {
                  console.log('Image clicked! Index:', index);
                  openModal(index);
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              
              {/* Overlay with expand icon */}
              <div 
                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center"
                onClick={() => {
                  console.log('Overlay clicked! Index:', index);
                  openModal(index);
                }}
              >
                <PhotographIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </div>

              {/* Image counter for multiple images */}
              {index === 5 && images.length > 6 && (
                <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-sm px-2 py-1 rounded-full">
                  +{images.length - 6}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Full Screen Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex items-center justify-center"
          onClick={closeModal}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
          >
            <XIcon className="h-8 w-8" />
          </button>

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
              >
                <ChevronLeftIcon className="h-12 w-12" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
              >
                <ChevronRightIcon className="h-12 w-12" />
              </button>
            </>
          )}

          {/* Image Container */}
          <div 
            className="relative max-w-4xl max-h-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImageUrl}
              alt={`RFQ ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                console.log('Image failed to load:', currentImageUrl);
                e.target.style.display = 'none';
              }}
            />
            
            {/* Image Counter */}
            {images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full">
                {currentImageIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery; 